import {
  getStateFromPath,
  LinkingOptions,
  type NavigationState,
  type PartialState,
} from "@react-navigation/native"
import { RootStackParamList } from "./types.ts"
import { getPathname, getSearchString } from "../utils/searchParams"
import { shouldAllowWebPaymentRequest } from "../utils/shouldAllowLogIn"
import { hasStoredAccessTokenSync } from "../utils/storedAccessToken"

// Custom-scheme links (synchpay://PaymentRequest/…) reach us without a leading
// slash, unlike https links, so match on the bare path and restore the prefix.
function normalizeLinkedPath(path: string) {
  const paymentRequestPrefix = "paymentrequest"
  const recurringPaymentPrefix = "recurringpayment"
  const leadingSlash = path.startsWith("/") ? "/" : ""
  const barePath = path.slice(leadingSlash.length)
  const lowerPath = barePath.toLowerCase()

  if (lowerPath.startsWith(paymentRequestPrefix)) {
    return leadingSlash + "PaymentRequest" + barePath.slice(paymentRequestPrefix.length)
  }

  if (lowerPath.startsWith(recurringPaymentPrefix)) {
    return leadingSlash + "RecurringPayment" + barePath.slice(recurringPaymentPrefix.length)
  }

  return path
}

export const nativeLinking: LinkingOptions<RootStackParamList> = {
  // Must stay in sync with the Android app-link hosts in AndroidManifest.xml
  // and the applinks entries in the iOS entitlements.
  prefixes: [
    "https://synchpay.com",
    "https://www.synchpay.com",
    "synchpay://",
    "https://app.synchpay.com",
    "https://www.app.synchpay.com",
    "https://app.trysynch.com",
    "https://app.stage.trysynch.com",
  ],
  config: {
    screens: {
      PaymentMethod: "PaymentRequest/:paymentRequestId",
      PlanConsentRequest: "RecurringPayment/:planConsentId",
      CharityFlow: "CharityFlow",
    },
  },
  getStateFromPath: (path, options) => {
    return getStateFromPath(normalizeLinkedPath(path), options)
  },
}

function isAuthGatedWebPath(path: string): boolean {
  const lower = path.toLowerCase()
  return lower.includes("/recurringpayment") || lower.includes("/debitauth")
}

function getLoggedOutAuthGatedPathState(path: string): PartialState<NavigationState> | undefined {
  if (hasStoredAccessTokenSync() || !isAuthGatedWebPath(path)) {
    return undefined
  }

  return {
    routes: [{ name: "LogIn" }],
  }
}

function getUnsupportedWebPaymentRequestState(
  path: string
): PartialState<NavigationState> | undefined {
  if (!path.toLowerCase().includes("/paymentrequest") || shouldAllowWebPaymentRequest()) {
    return undefined
  }

  return {
    routes: [{ name: "NotSupportedOnWeb" }],
  }
}

export const webLinking: LinkingOptions<RootStackParamList> = {
  prefixes: [
    "https://synchpay.com",
    "synchpay://",
    "https://app.synchpay.com",
    "https://app.trysynch.com",
    "https://app.stage.trysynch.com",
    "http://localhost:8080",
    "https://localhost:8080",
  ],
  config: {
    screens: {
      CharityFlow: "CharityFlow",
      SignUpFlow: "register/:registrationId",
      PlaidAuthLink: {
        screens: {
          HeadsUpScreen: "ExternalAuth",
        },
      },
      PaymentMethod: "PaymentRequest/:paymentRequestId",
      PlanConsentRequest: "RecurringPayment/:planConsentId",
      DebitAuthPayment: "DebitAuth/:debitAuthId",
      PaymentSentSuccessScreen: "successCreditCardPayment/:isFirstPostOnboardingPayment",
    },
  },
  getPathFromState: () => {
    const pathName = getPathname()
    const searchString = getSearchString()
    return pathName + searchString || "/"
  },
  getStateFromPath: (path, options) => {
    const unsupportedPaymentRequestState = getUnsupportedWebPaymentRequestState(path)
    if (unsupportedPaymentRequestState) {
      return unsupportedPaymentRequestState
    }

    const loggedOutState = getLoggedOutAuthGatedPathState(path)
    if (loggedOutState) {
      return loggedOutState
    }

    return getStateFromPath(normalizeLinkedPath(path), options)
  },
}
